import "react-datepicker/dist/react-datepicker.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import { config, getConfigWithToken } from "../config/config";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import formatDate from "../utils/formatDate";
import { formatDateOnly } from "../utils/formatDateOnly";
import { toast, ToastContainer } from "react-toastify";
import CarePlanCard from "../components/CarePlanCard";

const ClientDetail = () => {
  const location = useLocation();

  const [appointmentsPerPage] = useState(10);
  const [client, setClient] = useState(location.state?.client || null);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paginateAppointments, setPaginateAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    location.state?.selectedDate || null
  );
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [totalSessions, setTotalSessions] = useState(0);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Profile");
  const [diagnosisAndCarePlans, setDiagnosisAndCarePlans] = useState(null);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);

  const { id } = useParams();

  const navigate = useNavigate();
  const user = useSelector((state) => state?.user);

  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;

  const calculateAge = (dob) => {
    if (!dob) return null;

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    )
      age--;

    return age;
  };

  const clearDateFilter = () => setSelectedDate(null);

  const closeSummaryModal = () => {
    setSelectedSessionId(null);
    setShowSummaryModal(false);
    setSummaryData(null);
  };

  const fetchClientData = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/get-user/${id}`,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) setClient(response.data.data);
      else toast.error("Failed to fetch client data");
    } catch (error) {
      console.error("-> -> fetchClientData -> error->", error);
      toast.error(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const clientId = client._id ?? client.id;

      if (!clientId) {
        toast.error("No client ID available.");
        return;
      }

      setSessionsLoading(true);

      const params = new URLSearchParams();
      if (user.user?.providerId) {
        params.set("specialistId", user.user.providerId);
      }
      params.set("limit", String(appointmentsPerPage));
      params.set("skip", String((currentPage - 1) * appointmentsPerPage));
      if (statusFilter && statusFilter !== "All") {
        params.set("status", statusFilter);
      }
      if (selectedDate) {
        const y = selectedDate.getFullYear();
        const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
        const d = String(selectedDate.getDate()).padStart(2, "0");
        params.set("date", `${y}-${m}-${d}`);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/appointment/getAppointmentsByUser/${clientId}?${params.toString()}`,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        const sessionsData = response.data.data ?? [];

        const transformedSessions = sessionsData.map((session) => ({
          ...session,
          _id: session._id ?? session.id ?? Math.random().toString(),
          appointmentdate:
            session.appointmentdate ?? session.date ?? session.appointmenttime,
          appointmenttime:
            session.appointmenttime ?? session.appointmentdate ?? session.date,
          status: session.status ?? "Scheduled",
        }));

        setSessions(transformedSessions);
        const serverTotal =
          response.data.total ?? response.data.totalCount ?? null;
        setTotalSessions(
          Number.isFinite(serverTotal) ? serverTotal : transformedSessions.length
        );
      } else {
        toast.error("Failed to fetch sessions data.");
      }
    } catch (error) {
      console.error("-> -> fetchSessions -> error->", error);
      toast.error(error.response?.data?.message || "Failed to fetch sessions data.");
    } finally {
      setSessionsLoading(false);
    }
  };

  const filterSessionsByDate = (sessions, date) => {
    if (!date) return sessions;

    const selectedDateStr = date.toISOString().split("T")[0];

    return sessions.filter((session) => {
      try {
        const sessionDate = new Date(
          session.appointmenttime || session.appointmentdate || session.date
        );

        if (isNaN(sessionDate.getTime())) return false;

        const sessionDateStr = sessionDate.toISOString().split("T")[0];

        return sessionDateStr === selectedDateStr;
      } catch (error) {
        console.error("-> -> filterSessionsByDate -> error->", error);

        return false;
      }
    });
  };

  const fetchTranscriptionSummary = async (appointmentId) => {
    try {
      setSummaryLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/transcription/get-ai-summary/${appointmentId}`,
        getConfigWithToken(user.user?.token)
      );

      if (response.data) {
        setShowSummaryModal(true);
        setSummaryData(response.data);
      } else {
        toast.error("No summary data available for this session.");
      }
    } catch (error) {
      console.error("-> -> fetchTranscriptionSummary -> error->", error);
      toast.error(error.response.data.message);
    } finally {
      setSummaryLoading(false);
    }
  };

  const filterSessionsByStatus = (sessions, status) => {
    if (status === "All") return sessions;

    return sessions.filter((session) => {
      const sessionStatus = session.status ?? "Scheduled";

      return sessionStatus === status;
    });
  };

  const getFilteredSessions = () => sessions;

  const getStatusBadge = (status) => {
    const statusConfig = {
      "No Show": { text: "No Show", bg: "#FEE4E6", color: "#ED3A3A" },
      Active: { text: "Active", bg: "#d4edda", color: "#155724" },
      Completed: { text: "Completed", bg: "#D0FAE5", color: "#006145" },
      Inactive: { text: "Inactive", bg: "#e2e3e5", color: "#383d41" },
      Scheduled: { text: "Pending", bg: "#FFFED8", color: "#687000" },
      Upcoming: { text: "Upcoming", bg: "#FFFED8", color: "#687000" },
      Cancelled: { text: "Cancelled", bg: "#EDE9FE", color: "#5D0EC1" },
      canceled_by_user: { text: "Cancelled", bg: "#EDE9FE", color: "#5D0EC1" },
      late_cancelled: { text: "Late Cancel", bg: "#FEE9C6", color: "#973B00" },
    };

    const config = statusConfig[status] ?? {
      text: status ?? "Unknown",
      variant: "secondary",
      bg: "#e2e3e5",
      color: "#383d41"
    };

    return (
      <Badge
        bg={config.bg}
        pill
        style={{
          height: "19px",
          width: "92px",
          fontWeight: "500",
          backgroundColor: config.bg,
          color: config.color,
        }}
      >
        {config.text}
      </Badge>
    );
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setShowDatePicker(false);
  };

  const handleViewSession = (session) => {
    const sessionId = session._id ?? session.id;
    fetchTranscriptionSummary(sessionId);
    setSelectedSessionId(sessionId);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const refreshSessions = () => fetchSessions();

  const fetchDiagnosisAndCarePlans = async () => {
    const userId = client?._id ?? client?.id;
    if (!userId) {
      return;
    }

    try {
      setDiagnosisLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/get-diagnosis-and-care-plans/${userId}`,
        getConfigWithToken(user.user?.token)
      );
      if (response.data.success && response.data.data) {
        setDiagnosisAndCarePlans(response.data.data);
        return response.data.data; // Return data for CarePlanCard refresh
      }
    } catch (error) {
      console.error("Error fetching diagnosis and care plans:", error);
      toast.error("Failed to fetch diagnosis and care plans");
    } finally {
      setDiagnosisLoading(false);
    }
  };

  const age = calculateAge(client.dob);
  const filteredSessions = getFilteredSessions();

  useEffect(() => {
    if (!client && id) fetchClientData();
  }, [client, id]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDate, statusFilter]);

  useEffect(() => {
    if (client) {
      const clientId = client._id ?? client.id;
      if (clientId) fetchSessions();
    }
  }, [client, currentPage, selectedDate, statusFilter]);

  useEffect(() => {
    setPaginateAppointments(sessions);
  }, [sessions]);

  useEffect(() => {
    // Fetch diagnosis and care plans when Profile or Care Plan tab is clicked (only if not already fetched)
    if ((activeTab === "Profile" || activeTab === "Care Plan") && client && !diagnosisAndCarePlans && !diagnosisLoading) {
      fetchDiagnosisAndCarePlans();
    }
  }, [activeTab, client?._id, client?.id]);

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center py-5">
          <Spinner animation="border" size="lg" variant="primary" />

          <div className="mt-3 text-muted">Loading client information...</div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="container mt-4">
        <Alert variant="danger">
          <Alert.Heading>Client Not Found</Alert.Heading>

          <p>
            Unable to load client information. Please try again or contact
            support.
          </p>

          <Button variant="outline-danger" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left me-2"></i>Go Back
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div
      className="container-fluid mt-4"
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        padding: "20px",
      }}>
        <ToastContainer />
      <div className="mb-3" style={{ width: "50%", minWidth: "600px" }}>
        <div className="d-flex flex-wrap gap-2">
          <Button
            onClick={() => setActiveTab("Profile")}
            style={{
              minWidth: "140px",
              fontWeight: 600,
              borderRadius: "8px",
              backgroundColor: activeTab === "Profile" ? "#2EC4B6" : "#FFFFFF",
              color: activeTab === "Profile" ? "#FFFFFF" : "#003049",
              border: activeTab === "Profile" ? "none" : "1px solid #e9ecef",
              boxShadow: activeTab === "Profile" ? "0 2px 8px rgba(46, 196, 182, 0.3)" : "0 1px 3px rgba(0,0,0,0.1)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (activeTab !== "Profile") {
                e.target.style.backgroundColor = "#f8f9fa";
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== "Profile") {
                e.target.style.backgroundColor = "#FFFFFF";
              }
            }}>
            Profile
          </Button>
          <Button
            onClick={() => setActiveTab("Session History")}
            style={{
              minWidth: "140px",
              fontWeight: 600,
              borderRadius: "8px",
              backgroundColor: activeTab === "Session History" ? "#2EC4B6" : "#FFFFFF",
              color: activeTab === "Session History" ? "#FFFFFF" : "#003049",
              border: activeTab === "Session History" ? "none" : "1px solid #e9ecef",
              boxShadow: activeTab === "Session History" ? "0 2px 8px rgba(46, 196, 182, 0.3)" : "0 1px 3px rgba(0,0,0,0.1)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (activeTab !== "Session History") {
                e.target.style.backgroundColor = "#f8f9fa";
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== "Session History") {
                e.target.style.backgroundColor = "#FFFFFF";
              }
            }}>
            Session History
          </Button>
          <Button
            onClick={() => setActiveTab("Care Plan")}
            style={{
              minWidth: "140px",
              fontWeight: 600,
              borderRadius: "8px",
              backgroundColor: activeTab === "Care Plan" ? "#2EC4B6" : "#FFFFFF",
              color: activeTab === "Care Plan" ? "#FFFFFF" : "#003049",
              border: activeTab === "Care Plan" ? "none" : "1px solid #e9ecef",
              boxShadow: activeTab === "Care Plan" ? "0 2px 8px rgba(46, 196, 182, 0.3)" : "0 1px 3px rgba(0,0,0,0.1)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (activeTab !== "Care Plan") {
                e.target.style.backgroundColor = "#f8f9fa";
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== "Care Plan") {
                e.target.style.backgroundColor = "#FFFFFF";
              }
            }}>
            Care Plan
          </Button>
        </div>
      </div>
      <Card
        className="mb-4"
        style={{
          border: "none",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          width: "50%",
          minWidth: "600px",
        }}>
        <Card.Body className="p-3" style={{ textAlign: "left" }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              width: "100%",
              textAlign: "left",
            }}>
            <div
              style={{
                alignItems: "center",
                backgroundColor: "#27B99A",
                borderRadius: "50%",
                boxShadow: "0 4px 15px rgba(39, 185, 154, 0.3)",
                color: "white",
                display: "flex",
                fontSize: "32px",
                fontWeight: "600",
                height: "80px",
                justifyContent: "center",
                width: "80px",
                flexShrink: 0,
              }}>
              {client.name ? client.name.charAt(0).toUpperCase() : "U"}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                alignItems: "flex-start",
                textAlign: "left",
                flex: 1,
              }}>
              <h2
                style={{
                  backgroundColor: "white",
                  color: "#2C5F7C",
                  fontFamily: "Poppins",
                  fontSize: "22px",
                  fontWeight: "700",
                  margin: 0,
                  padding: 0,
                  lineHeight: 1.2,
                  textAlign: "left",
                }}>
                {client.name ?? "N/A"}
              </h2>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  width: "100%",
                  justifyContent: "flex-start",
                }}>
                <i
                  className="fas fa-shield-check text-primary"
                  style={{ color: "#27B99A", fontSize: "14px" }}></i>
                <span style={{ color: "#666", fontSize: "14px" }}>
                  {formatDate(client.dob)} {age && `(${age})`}
                </span>
              </div>

              <div style={{ width: "100%" }}>
                {getStatusBadge(
                  client.status ?? (client.isactive ? "Active" : "Inactive")
                )}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {activeTab === "Profile" && (
      <Card
        style={{
          border: "none",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          width: "50%",
          minWidth: "600px",
          height: "400px",
        }}>
          <Card.Body className="p-0" style={{ height: "100%" }}>
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "20px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}>
              <Row className="align-items-center mb-3">
                <Col>
                  <h3
                    style={{
                      color: "#1f2d3d",
                      fontFamily: "Poppins",
                      fontSize: "24px",
                      fontWeight: "700",
                      margin: 0,
                      textAlign: "left",
                    }}>
                    Diagnosis
                  </h3>
                </Col>
                <Col xs="auto">
                  <button
                    disabled={diagnosisLoading}
                    onClick={fetchDiagnosisAndCarePlans}
                    style={{
                      border: "none",
                      backgroundColor: "transparent",
                      padding: "4px 8px",
                      color: "#1f2d3d",
                      cursor: diagnosisLoading ? "wait" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    type="button">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="26"
                      height="26"
                      viewBox="0 0 26 26"
                      fill="none"
                      style={{
                        width: "26px",
                        height: "26px",
                        cursor: diagnosisLoading ? "wait" : "pointer",
                        transform: diagnosisLoading ? "rotate(360deg)" : "rotate(0deg)",
                        transition: diagnosisLoading ? "transform 1s linear infinite" : "transform 0.3s ease",
                      }}
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.65 0C9.1 0 5.07 2.53333 2.99 6.4L0 3.33333V12H8.45L4.81 8.26667C6.5 4.93333 9.75 2.66667 13.65 2.66667C18.98 2.66667 23.4 7.2 23.4 12.6667C23.4 18.1333 18.98 22.6667 13.65 22.6667C9.36 22.6667 5.85 19.8667 4.42 16H1.69C3.12 21.3333 7.93 25.3333 13.65 25.3333C20.54 25.3333 26 19.6 26 12.6667C26 5.73333 20.41 0 13.65 0ZM11.7 6.66667V13.4667L17.81 17.2L18.85 15.4667L13.65 12.2667V6.66667H11.7Z"
                        fill="black"
                      />
                    </svg>
                  </button>
                </Col>
              </Row>

              {/* Green line below Diagnosis heading */}
              <div
                style={{
                  borderTop: "2px solid #2EC4B6",
                  marginBottom: "24px",
                  width: "100%",
                }}></div>

              {diagnosisLoading ? (
                <div 
                  className="text-center py-5"
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "40px",
                  }}>
                  <Spinner animation="border" variant="primary" />
                  <div className="mt-3 text-muted">Loading diagnosis...</div>
                </div>
              ) : diagnosisAndCarePlans?.diagnosis?.active ? (
                <div>
                  {["Primary", "Secondary", "Tertiary"].map((label, idx) => {
                    const entry = diagnosisAndCarePlans.diagnosis.active[idx] || {};
                    const hasValue = entry.code && entry.description;
                    return (
                      <div key={label} style={{ padding: "16px 0" }}>
                        <Row className="align-items-start">
                          <Col>
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: 700,
                                color: "#1f2d3d",
                                marginBottom: "8px",
                                textAlign: "left",
                              }}>
                              {label}
                            </div>
                            <div
                              style={{
                                color: "#1f2d3d",
                                fontSize: "16px",
                                fontWeight: 400,
                                textAlign: "left",
                              }}>
                              {hasValue ? `${entry.code} - ${entry.description}` : "—"}
                            </div>
                          </Col>
                          <Col xs="auto" style={{ textAlign: "right" }}>
                            <div
                              style={{
                                color: "#1f2d3d",
                                fontSize: "16px",
                                fontWeight: 700,
                                marginBottom: "4px",
                                textAlign: "right",
                              }}>
                              Effective Date
                            </div>
                            <div
                              style={{
                                color: "#1f2d3d",
                                fontSize: "16px",
                                fontWeight: 400,
                                textAlign: "right",
                              }}>
                              {entry.effectiveDate ? formatDateOnly(entry.effectiveDate) : "—"}
                            </div>
                          </Col>
                        </Row>
                        {idx < 2 && (
                          <div
                            style={{
                              borderTop: "1px solid #e5e7eb",
                              marginTop: "16px",
                            }}></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div 
                  className="text-center py-5"
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "40px",
                  }}>
                  <div className="text-muted">No diagnosis data available</div>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {activeTab === "Care Plan" && (
        <div style={{ width: "100%" }}>
          <CarePlanCard 
            userId={client?._id ?? client?.id} 
            initialData={diagnosisAndCarePlans}
            onRefresh={fetchDiagnosisAndCarePlans}
          />
        </div>
      )}

      {activeTab === "Session History" && (
        <Card
          style={{
            border: "none",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            width: "100%",
            height: "auto",
        }}>
        <Card.Body className="p-4">
          <Row className="align-items-center mb-4">
            <Col>
              <h3
                style={{
                  color: "#2C5F7C",
                  fontFamily: "Poppins",
                  fontSize: "24px",
                  fontWeight: "700",
                  margin: 0,
                  textAlign: "start",
                }}>
                <i
                  className="fas fa-history me-2"
                  style={{ color: "#27B99A" }}></i>
                Session History
                {totalSessions > 0 && (
                  <Badge bg="light" className="ms-2" text="dark">
                    {totalSessions} session{totalSessions !== 1 ? "s" : ""}
                  </Badge>
                )}
              </h3>
            </Col>

            <Col xs="auto">
              <div className="d-flex gap-3">
                <Button
                  disabled={sessionsLoading}
                  onClick={refreshSessions}
                  style={{
                    border: "2px solid #e9ecef",
                    borderRadius: "8px",
                    fontWeight: "500",
                    padding: "8px 16px",
                  }}
                  variant="outline-primary">
                  <i
                    className={`fas fa-sync-alt ${
                      sessionsLoading ? "fa-spin" : ""
                    } me-2`}></i>
                  Refresh
                </Button>

                <div className="position-relative">
                  <Button
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    style={{
                      border: "2px solid #e9ecef",
                      borderRadius: "8px",
                      fontWeight: "500",
                      minWidth: "140px",
                      padding: "8px 16px",
                    }}
                    variant="outline-secondary">
                    <i className="fas fa-calendar me-2"></i>

                    {selectedDate ? formatDate(selectedDate) : "Select Date"}

                    {selectedDate && (
                      <i
                        className="fas fa-times ms-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearDateFilter();
                        }}
                        style={{ color: "#dc3545", cursor: "pointer" }}></i>
                    )}
                  </Button>

                  {showDatePicker && (
                    <div
                      className="position-absolute"
                      style={{ left: 0, top: "100%", zIndex: 1000 }}>
                      <DatePicker
                        inline
                        maxDate={new Date()}
                        onChange={handleDateSelect}
                        selected={selectedDate}
                      />
                    </div>
                  )}
                </div>

                <select
                  className="form-select"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    border: "2px solid #e9ecef",
                    borderRadius: "8px",
                    fontWeight: "500",
                    minWidth: "140px",
                    padding: "8px 16px",
                  }}
                  value={statusFilter}>
                  <option value="All">All Statuses</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Completed">Completed</option>
                  <option value="No Show">No Show</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Upcoming">Upcoming</option>
                </select>
              </div>
            </Col>
          </Row>

          {error && (
            <Alert className="mb-3" variant="danger">
              <i className="fas fa-exclamation-triangle me-2"></i>

              {error}

              <Button
                className="ms-3"
                onClick={refreshSessions}
                size="sm"
                variant="outline-danger">
                Retry
              </Button>
            </Alert>
          )}

          <div className="table-responsive">
            {sessionsLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />

                <div className="mt-3 text-muted">Loading sessions...</div>
              </div>
            ) : (
              <>
                <Table hover style={{ marginBottom: 0 }}>
                  <thead
                    style={{
                      backgroundColor: "#2C5F7C",
                      borderRadius: "8px",
                      color: "white",
                    }}>
                    <tr>
                      <th
                        style={{
                          
                          padding: "16px",
                        }}>
                        <i className="fas fa-hashtag me-2"></i>Session ID
                      </th>

                      <th style={{ padding: "16px" }}>
                        <i className="fas fa-calendar me-2"></i>Session Date
                      </th>

                      <th style={{ padding: "16px" }}>
                        <i className="fas fa-clock me-2"></i>Time
                      </th>

                      <th style={{ padding: "16px" }}>Duration</th>

                      <th style={{ padding: "16px" }}>
                        <i className="fas fa-info-circle me-2"></i>Status
                      </th>

                      <th
                        style={{
                          padding: "16px",
                        }}>
                        <i className="fas fa-cogs me-2"></i>Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginateAppointments.length > 0 ? (
                      paginateAppointments.map((session, index) => {
                        const endTime = new Date(session.appointmentEndTime);
                        const now = new Date();
                        // if session status is upcoming and time is not passed, set status to "Scheduled", otherwise status to "Completed"
                        const newStatus = session.status === "Upcoming" ? now >= endTime ? "Completed" : "Scheduled" : session.status;

                        return (
                          <tr
                          key={session._id || index}
                          style={{ borderBottom: "1px solid #f8f9fa" }}>
                          <td
                            style={{
                              color: "#2C5F7C",
                              fontWeight: "600",
                              padding: "16px",
                            }}>
                            {(index + 1).toString().padStart(4, "0")}
                          </td>

                          <td style={{ padding: "16px" }}>
                            {(() => {
                              // Handle appointmenttime (datetime) - extract date part and format directly
                              if (session.appointmenttime) {
                                const dateStr = session.appointmenttime;
                                // Extract YYYY-MM-DD from ISO string
                                const isoMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
                                if (isoMatch) {
                                  return formatDateOnly(isoMatch[1]);
                                }
                                // Fallback to formatDate if not ISO format
                                return formatDate(dateStr, "date");
                              }
                              // Handle appointmentdate (date-only string)
                              if (session.appointmentdate) {
                                return formatDateOnly(session.appointmentdate);
                              }
                              return "N/A";
                            })()}
                          </td>

                          <td style={{ padding: "16px" }}>
                            {session.appointmenttime
                              ? formatDate(session.appointmenttime, "time")
                              : "N/A"}
                          </td>

                          <td style={{ padding: "16px" }}>
                            {session.sessionLength}m
                          </td>

                          <td style={{ padding: "16px" }}>
                            {getStatusBadge(newStatus)}
                          </td>

                          <td style={{ padding: "16px" }}>
                            <Button
                              className="app-view-btn"
                              onClick={() => handleViewSession(session)}
                              size="sm"
                              variant="success">
                              <i className="fas fa-eye me-1"></i>View
                            </Button>
                          </td>
                        </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-5">
                          <div style={{ fontSize: "18px", color: "#6c757d" }}>
                            <i
                              className="fas fa-inbox fa-2x mb-3"
                              style={{ color: "#dee2e6" }}></i>
                            <br />
                            {totalSessions === 0 ? (
                              <>
                                <strong>
                                  No sessions found for this client
                                </strong>

                                <br />

                                <small className="text-muted">
                                  This client hasn't had any sessions yet.
                                </small>
                              </>
                            ) : selectedDate || statusFilter !== "All" ? (
                              <>
                                <strong>
                                  No sessions found for the selected filters
                                </strong>

                                <br />

                                <small className="text-muted">
                                  Try adjusting your date or status filters.
                                </small>
                              </>
                            ) : (
                              <>
                                <strong>No sessions available</strong>

                                <br />

                                <small className="text-muted">
                                  Unable to load session data.
                                </small>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <TablePaginationBar
                  currentPage={currentPage}
                  totalItems={totalSessions}
                  rowsPerPage={appointmentsPerPage}
                  onPageChange={paginate}
                />
              </>
            )}
          </div>
        </Card.Body>
      </Card>
      )}

      <Modal
        centered
        onHide={closeSummaryModal}
        show={showSummaryModal}
        size="lg"
        style={{ zIndex: 1050 }}>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: "#2C5F7C",
            borderBottom: "none",
            color: "white",
          }}>
          <Modal.Title>
            <i className="fas fa-file-alt me-2"></i>
            Session Summary
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "24px" }}>
          {summaryLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />

              <div className="mt-3 text-muted">Loading session summary...</div>
            </div>
          ) : summaryData ? (
            <div>
              {summaryData.content && (
                <div className="mb-4">
                  <h5
                    style={{
                      color: "#2C5F7C",
                      fontFamily: "Poppins",
                      fontWeight: "600",
                      marginBottom: "16px",
                    }}>
                    <i
                      className="fas fa-clipboard-list me-2"
                      style={{ color: "#27B99A" }}></i>
                    Session Summary
                  </h5>

                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      lineHeight: "1.6",
                      padding: "16px",
                      whiteSpace: "pre-wrap",
                    }}>
                    {summaryData.content}
                  </div>
                </div>
              )}

              {summaryData.actionItems && (
                <div>
                  <h5
                    style={{
                      color: "#2C5F7C",
                      fontFamily: "Poppins",
                      fontWeight: "600",
                      marginBottom: "16px",
                    }}>
                    <i
                      className="fas fa-tasks me-2"
                      style={{ color: "#27B99A" }}></i>
                    Action Items
                  </h5>

                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      lineHeight: "1.6",
                      padding: "16px",
                      whiteSpace: "pre-wrap",
                    }}>
                    {summaryData.actionItems}
                  </div>
                </div>
              )}

              {!summaryData.content && !summaryData.actionItems && (
                <div className="text-center py-4">
                  <i
                    className="fas fa-file-alt fa-3x mb-3"
                    style={{ color: "#dee2e6" }}></i>

                  <h5 style={{ color: "#6c757d" }}>No Summary Available</h5>

                  <p className="text-muted">
                    This session doesn't have a summary or action items yet.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <i
                className="fas fa-exclamation-triangle fa-3x mb-3"
                style={{ color: "#ffc107" }}></i>

              <h5 style={{ color: "#6c757d" }}>Error Loading Summary</h5>

              <p className="text-muted">
                Unable to load the session summary. Please try again.
              </p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #e9ecef" }}>
          <Button
            onClick={closeSummaryModal}
            style={{
              backgroundColor: "#6c757d",
              border: "none",
              borderRadius: "6px",
              fontWeight: "500",
              padding: "8px 20px",
            }}
            variant="secondary">
            <i className="fas fa-times me-2"></i>Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ClientDetail;
